/**
 * Single source of truth for "what status is leg N in?".
 *
 * Every UI surface that shows a leg status — the leg tabs on the order detail
 * page, the Update Leg Status modal, the confirm gates — must read this so the
 * same leg never reads differently in two places.
 *
 * Signals, highest rank wins:
 *  - the leg's own `driver_status`
 *  - the leg's active EAPI trip status, mapped to an order status
 *  - the parent order's status, which on a multi-leg order is an aggregate: up
 *    to `confirmed` it passes through, anything past that is discarded (falls
 *    back to `booked`), because a parent flips to in_transit/delivered off
 *    ANY/ALL legs and letting it through would drag unstarted legs forward
 *
 * `canceled` affects billing, so only the TMS's own records can produce it:
 * the leg's `driver_status` or the parent order's status. An EAPI trip that
 * reads cancelled upstream (e.g. trip_type → Manual cancels the trip there)
 * is ignored as a cancellation signal — the leg falls back to its other
 * signals instead.
 */
import { mapEapiStatusToOrderStatus } from "@/lib/eapi-status-mapper";

export const LEG_STATUS_RANK: Record<string, number> = {
  booked: 0,
  confirmed: 1,
  tender_received: 2,
  in_transit: 3,
  delivered: 4,
  canceled: 5,
};

export interface ResolvableLeg {
  driver_status?: string | null;
  eapi_status?: string | null;
}

/**
 * The parent status as evidence about one leg.
 *
 * Multi-leg: the parent is a rollup off *some* leg, so it says nothing about
 * this one — EXCEPT `canceled`, which is an ATMS decision about the whole
 * order and applies to every leg. Everything else is treated as null and the
 * leg earns a status of its own (no signal at all = `booked`). This is the
 * deliberate inverse of the EAPI rule above: the trip contributes everything
 * *but* canceled, the parent contributes *only* canceled.
 *
 * Single-leg: the stored order status IS that leg's status, so it passes
 * through untouched.
 */
export function parentStatusForLeg(
  parentStatus: string | null | undefined,
  isMultiLeg: boolean,
): string | undefined {
  if (!isMultiLeg) return parentStatus ?? undefined;
  return parentStatus === "canceled" ? "canceled" : undefined;
}

export function resolveLegStatus(
  leg: ResolvableLeg | null | undefined,
  parentStatus: string | undefined | null,
  isMultiLeg: boolean = false,
): string | undefined {
  const eapiRaw = leg?.eapi_status
    ? mapEapiStatusToOrderStatus(String(leg.eapi_status))
    : null;
  const eapiMapped = eapiRaw === "canceled" ? null : eapiRaw;
  const driver = leg?.driver_status ?? null;
  const parentForLeg = parentStatusForLeg(parentStatus, isMultiLeg);

  const candidates: Array<string | null | undefined> = [eapiMapped, parentForLeg, driver];
  let best: string | undefined;
  let bestRank = -1;
  for (const s of candidates) {
    if (!s) continue;
    const r = LEG_STATUS_RANK[s] ?? -1;
    if (r > bestRank) {
      bestRank = r;
      best = s;
    }
  }
  // A multi-leg leg with no signal of its own is simply not started.
  if (best === undefined && isMultiLeg && leg) return "booked";
  return best;
}
